
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { ClubMessage } from './types';
import MessagesList from './MessagesList';
import MessageInput from './MessageInput';
import { MessageSquare } from 'lucide-react';

interface ClubGroupChatProps {
  clubId: string;
  clubName: string;
}

const ClubGroupChat: React.FC<ClubGroupChatProps> = ({ clubId, clubName }) => {
  const [messages, setMessages] = useState<ClubMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  console.log('ClubGroupChat rendering with:', { clubId, clubName, user: user?.id });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      console.log('Fetching messages for club:', clubId);
      setLoading(true);
      
      const { data, error } = await supabase
        .from('club_messages')
        .select(`
          *,
          profiles:user_id (id, full_name, username, avatar_url)
        `)
        .eq('club_id', clubId)
        .order('created_at', { ascending: true });

      console.log('Messages fetch result:', { data, error });

      if (error) {
        console.error('Error fetching messages:', error);
        toast({
          title: "Error",
          description: "Failed to load messages. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        const processedMessages = data.map(message => ({
          ...message,
          author: message.profiles
        }));
        console.log('Processed messages:', processedMessages);
        setMessages(processedMessages);
      }
    } catch (error) {
      console.error('Error in fetchMessages:', error);
      toast({
        title: "Error",
        description: "Failed to load messages",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (content: string) => {
    if (!user || !content.trim()) {
      console.log('Cannot send message - no user or empty content');
      return;
    }

    try {
      console.log('Sending message:', { clubId, userId: user.id, content });
      
      const { error } = await supabase
        .from('club_messages')
        .insert({
          club_id: clubId,
          user_id: user.id,
          content: content.trim(),
          message_type: 'text'
        });

      if (error) {
        console.error('Error sending message:', error);
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
        return;
      }

      console.log('Message sent successfully');
      // Message will be added via realtime subscription
    } catch (error) {
      console.error('Error in sendMessage:', error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (!clubId || !user) {
      console.log('Missing clubId or user, skipping message fetch');
      return;
    }

    fetchMessages();

    // Set up realtime subscription
    console.log('Setting up realtime subscription for club:', clubId);
    const channel = supabase
      .channel(`club_messages_${clubId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'club_messages',
        filter: `club_id=eq.${clubId}`
      }, async (payload) => {
        console.log('Realtime INSERT received:', payload);
        
        // Fetch the complete message with author info
        const { data: newMessageData } = await supabase
          .from('club_messages')
          .select(`
            *,
            profiles:user_id (id, full_name, username, avatar_url)
          `)
          .eq('id', payload.new.id)
          .single();
          
        if (newMessageData) {
          const processedMessage = {
            ...newMessageData,
            author: newMessageData.profiles
          };
          
          setMessages(prev => {
            // Check if message already exists to avoid duplicates
            if (prev.some(msg => msg.id === processedMessage.id)) {
              return prev;
            }
            return [...prev, processedMessage];
          });
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'club_messages',
        filter: `club_id=eq.${clubId}`
      }, () => {
        console.log('Realtime UPDATE received, refetching messages');
        fetchMessages();
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'club_messages',
        filter: `club_id=eq.${clubId}`
      }, () => {
        console.log('Realtime DELETE received, refetching messages');
        fetchMessages();
      })
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
      });

    return () => {
      console.log('Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [clubId, user]);

  if (!user) {
    return (
      <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg">
        <p className="text-muted-foreground">Please log in to access the group chat.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white border rounded-lg shadow-sm" style={{ height: '600px' }}>
      {/* Chat Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-gray-50">
        <MessageSquare className="h-5 w-5 text-blue-600" />
        <h3 className="font-semibold text-gray-900">{clubName} Group Chat</h3>
      </div>
      
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 bg-white">
        <MessagesList 
          messages={messages} 
          currentUserId={user?.id}
          loading={loading}
        />
        <div ref={messagesEndRef} />
      </div>
      
      {/* Message Input */}
      <div className="border-t p-4 bg-gray-50">
        <MessageInput onSendMessage={sendMessage} />
      </div>
    </div>
  );
};

export default ClubGroupChat;
